export const ALL_PERMS_DEFAULT_SITE_ID = 'atlantic-deer-island'

export interface OrgSiteCandidate {
  readonly id: string
  readonly tags: { readonly organization?: string }
}

export interface OrganizationCandidate {
  readonly id: string
}

export interface ActiveOrgSite {
  organizationId: string | undefined
  siteId: string | undefined
}

/** Org and site ids named in the matched route path. */
export interface RouteOrgSiteParams {
  orgOrSiteID?: string
  siteID?: string
  orgID?: string
}

/** The site the navbar falls back to when the URL names no valid org or site. */
export function pickDefaultSite<Site extends OrgSiteCandidate>(
  sites: readonly Site[]
): Site | undefined {
  return (
    sites.find((site) => site.id === ALL_PERMS_DEFAULT_SITE_ID) ?? sites.at(0)
  )
}

/** Internal-only routes (admin and health-check) where the navbar selects no org or site. */
export function isInternalRoute(pathname: string): boolean {
  return pathname.startsWith('/admin/') || pathname.startsWith('/health-check')
}

/**
 * The route entry the navbar honors first: a known site or org named in the
 * path, checked in the order orgOrSiteID, siteID, orgID. A site also sets its
 * org. An org sets no site.
 */
function resolveRouteEntry(
  route: RouteOrgSiteParams,
  organizations: readonly OrganizationCandidate[],
  sites: readonly OrgSiteCandidate[]
): ActiveOrgSite | undefined {
  for (const id of [route.orgOrSiteID, route.siteID, route.orgID]) {
    if (!id) continue
    const site = sites.find((candidate) => candidate.id === id)
    if (site) {
      return { organizationId: site.tags.organization, siteId: site.id }
    }
    const organization = organizations.find((candidate) => candidate.id === id)
    if (organization) {
      return { organizationId: organization.id, siteId: undefined }
    }
  }
  return undefined
}

/**
 * The org and site the navbar shows. Internal routes select neither. A valid
 * route entry wins, then a valid URL site, which fixes its own org. The URL
 * org applies only without a valid site. Otherwise the default site. A
 * conflicting URL org never pairs with a site's org.
 */
export function resolveActiveOrgSite({
  pathname,
  route = {},
  urlOrganizationId,
  urlSiteId,
  organizations,
  sites,
}: {
  pathname: string
  route?: RouteOrgSiteParams
  urlOrganizationId: string | undefined
  urlSiteId: string | undefined
  organizations: readonly OrganizationCandidate[]
  sites: readonly OrgSiteCandidate[]
}): ActiveOrgSite {
  if (isInternalRoute(pathname)) {
    return { organizationId: undefined, siteId: undefined }
  }

  const fromRoute = resolveRouteEntry(route, organizations, sites)
  if (fromRoute) {
    return fromRoute
  }

  const urlSite = sites.find((site) => site.id === urlSiteId)
  if (urlSite) {
    return { organizationId: urlSite.tags.organization, siteId: urlSite.id }
  }
  const urlOrganization = organizations.find(
    (organization) => organization.id === urlOrganizationId
  )
  if (urlOrganization) {
    return { organizationId: urlOrganization.id, siteId: undefined }
  }

  const defaultSite = pickDefaultSite(sites)
  return {
    organizationId: defaultSite?.tags.organization,
    siteId: defaultSite?.id,
  }
}
